import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
  Outlet,
} from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import StudentManagement from "./pages/StudentManagement";
import LeadManagement from "./pages/LeadManagement";
import Forms from "./pages/Forms";
import Attendance from "./pages/Attendance";
import Settings from "./pages/Settings";
import Staff from "./pages/Staff";
import Classes from "./pages/Classes";
import Events from "./pages/Events";
import Messaging from "./pages/Messaging";
import StudentSignIn from "./pages/StudentSignIn";
import Onboarding from "./pages/Onboarding";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import KioskLogin from "./pages/KioskLogin";
import SubAccountLogin from "./pages/SubAccountLogin";
import SMS from "./pages/SMS";
import SMSHistory from "./pages/SMSHistory";
import PushNotifications from "./pages/PushNotifications";
import Waivers from "./pages/Waivers";
import Store from "./pages/Store";
import PublicWaiver from "./pages/PublicWaiver";
import PublicEventRegistration from "./pages/PublicEventRegistration";
import PublicProductPurchase from "./pages/PublicProductPurchase";
import ProductPurchaseSuccess from "./pages/ProductPurchaseSuccess";
import PublicAppointmentBooking from "./pages/PublicAppointmentBooking";
import EventRegistrationSuccess from "./pages/EventRegistrationSuccess";
import PortalSuccess from "./pages/PortalSuccess";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import IntegrationsSettings from "./components/settings/IntegrationsSettings";
import ProfileSettings from "./components/settings/ProfileSettings";
import GeneralSettings from "./components/settings/GeneralSettings";
import BillingSettings from "./components/settings/BillingSettings";
import SecuritySettings from "./components/settings/SecuritySettings";
import SyncSettings from "./components/settings/SyncSettings";
import FamilyDiscounts from "./pages/settings/FamilyDiscounts";
import DiscountCodes from "./pages/settings/DiscountCodes";
import RequireOnboarding from "./components/RequireOnboarding";
import RequireSubscription from "./components/RequireSubscription";
import RequireActiveBilling from "./components/RequireActiveBilling";
import FinishingSetup from "./pages/FinishingSetup";
import UpgradeComplete from "./pages/UpgradeComplete";
import { StripeProvider } from "./contexts/StripeContext";
import ProtectedRoute from "./components/ProtectedRoute";
import PermissionRoute from "./components/PermissionRoute";
import SmartDefaultRoute from "./components/SmartDefaultRoute";
import GeoBlock from "./components/GeoBlock";
// Website pages
import WebsiteLayout from "./layouts/WebsiteLayout";
import Home from "./pages/website/Home";
import Pricing from "./pages/website/Pricing";
import Features from "./pages/website/Features";
import Terms from "./pages/website/Terms";
import Contact from "./pages/website/Contact";
import PrivacyPolicy from "./pages/website/PrivacyPolicy";
import DataMigration from "./pages/website/DataMigration";
import Blog from "./pages/website/Blog";
import BlogPost from "./pages/website/BlogPost";
import MetaLanding from "./pages/website/MetaLanding";
import Unsubscribe from "./pages/Unsubscribe";
import UpdatePayment from "./pages/UpdatePayment";
import ReportBug from "./pages/ReportBug";
import Updates from "./pages/Updates";

// App pages
import AppLayout from "./layouts/AppLayout";
import InactiveStudioLayout from "./layouts/InactiveStudioLayout";
import InactiveStudioSettings from "./pages/InactiveStudioSettings";
import KioskMode from "./pages/KioskMode";
import NotFound from "./pages/NotFound";
import { isKioskMode } from "./utils/kioskAuth";
import SocialMediaMeta from "./components/SocialMediaMeta";

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

function AppRoutes() {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Auth Routes - Routed under WebsiteLayout below for header/footer */}

      {/* Kiosk Route */}
      <Route path="/kiosk" element={<KioskLogin />} />

      {/* Protected App Routes */}
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        {/* Onboarding Routes - Protected but no subscription required */}
        <Route element={<RequireOnboarding />}>
          <Route path="onboarding" element={<Onboarding />} />
        </Route>

        {/* Setup completion route */}
        <Route path="finishing-setup" element={<FinishingSetup />} />
        <Route path="upgrade-complete" element={<UpgradeComplete />} />

        {/* Inactive Studio Routes - Protected but inactive subscription */}
        <Route
          path="inactive-studio"
          element={
            <RequireActiveBilling>
              <InactiveStudioLayout />
            </RequireActiveBilling>
          }
        >
          <Route path="settings" element={<InactiveStudioSettings />}>
            <Route path="billing" element={<BillingSettings />} />
          </Route>
        </Route>

        {/* App Routes - Protected and requires subscription */}
        <Route
          element={
            <RequireSubscription>
              <Outlet />
            </RequireSubscription>
          }
        >
          <Route index element={<SmartDefaultRoute />} />
          <Route
            path="dashboard"
            element={
              <PermissionRoute permission="dashboard">
                <Dashboard />
              </PermissionRoute>
            }
          />
          <Route
            path="students"
            element={
              <PermissionRoute permission="students">
                <StudentManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="staff"
            element={
              <PermissionRoute permission="staff">
                <Staff />
              </PermissionRoute>
            }
          />
          <Route
            path="classes-events"
            element={
              <PermissionRoute permission="classes">
                <Classes />
              </PermissionRoute>
            }
          />
          <Route
            path="events"
            element={
              <PermissionRoute permission="events">
                <Events />
              </PermissionRoute>
            }
          />
          <Route
            path="attendance"
            element={
              <PermissionRoute permission="attendance">
                <Attendance />
              </PermissionRoute>
            }
          />
          <Route
            path="messaging"
            element={
              <PermissionRoute permission="messaging">
                <Messaging />
              </PermissionRoute>
            }
          />
          <Route
            path="sms"
            element={
              <PermissionRoute permission="messaging">
                <SMS />
              </PermissionRoute>
            }
          />
          <Route
            path="sms/history"
            element={
              <PermissionRoute permission="messaging">
                <SMSHistory />
              </PermissionRoute>
            }
          />
          <Route
            path="push-notifications"
            element={
              <PermissionRoute permission="messaging">
                <PushNotifications />
              </PermissionRoute>
            }
          />
          <Route
            path="leads"
            element={
              <PermissionRoute permission="leads">
                <LeadManagement />
              </PermissionRoute>
            }
          />
          <Route
            path="forms"
            element={
              <PermissionRoute permission="forms">
                <Forms />
              </PermissionRoute>
            }
          />
          <Route
            path="waivers"
            element={
              <PermissionRoute permission="waivers">
                <Waivers />
              </PermissionRoute>
            }
          />
          <Route
            path="store"
            element={
              <PermissionRoute permission="store">
                <Store />
              </PermissionRoute>
            }
          />
          <Route
            path="documentation"
            element={<Navigate to="/app/dashboard" replace />}
          />
          <Route
            path="student-signin"
            element={
              <PermissionRoute permission="attendance">
                <StudentSignIn />
              </PermissionRoute>
            }
          />
          <Route
            path="student-sign-in"
            element={
              <PermissionRoute permission="attendance">
                <StudentSignIn />
              </PermissionRoute>
            }
          />
          <Route path="kiosk-mode" element={<KioskMode />} />
          <Route path="updates" element={<Updates />} />
          <Route path="report-bug" element={<ReportBug />} />
          <Route path="settings" element={<Settings />}>
            <Route index element={<Navigate to="profile" replace />} />
                          <Route path="profile" element={<ProfileSettings />} />
              <Route path="general" element={<GeneralSettings />} />
              <Route path="billing" element={<BillingSettings />} />
              <Route path="security" element={<SecuritySettings />} />
              <Route path="integrations" element={<IntegrationsSettings />} />
              <Route path="sync" element={<SyncSettings />} />
              <Route path="family-discounts" element={<FamilyDiscounts />} />
              <Route path="discount-codes" element={<DiscountCodes />} />
          </Route>
        </Route>
      </Route>

      {/* Public Website Routes */}
      <Route path="waiver/:waiverId" element={<PublicWaiver />} />
      <Route path="register/event/:eventId" element={<PublicEventRegistration />} />
      <Route path="buy/:productId" element={<PublicProductPurchase />} />
      <Route path="buy/:productId/success" element={<ProductPurchaseSuccess />} />
      <Route path="appointment/:leadId" element={<PublicAppointmentBooking />} />
      <Route path="event-registration-success" element={<EventRegistrationSuccess />} />
      <Route path="portal-success" element={<PortalSuccess />} />
      <Route path="unsubscribe" element={<Unsubscribe />} />
      <Route path="update-payment" element={<UpdatePayment />} />
      <Route path="/*" element={<WebsiteLayout />}>
        <Route
          index
          element={isAuthenticated ? <Navigate to="/app" replace /> : <Home />}
        />
        <Route
          path="features"
          element={
            isAuthenticated ? <Navigate to="/app" replace /> : <Features />
          }
        />
        <Route
          path="data-migration"
          element={
            isAuthenticated ? <Navigate to="/app" replace /> : <DataMigration />
          }
        />
        <Route
          path="pricing"
          element={
            isAuthenticated ? <Navigate to="/app" replace /> : <Pricing />
          }
        />
        <Route
          path="terms"
          element={isAuthenticated ? <Navigate to="/app" replace /> : <Terms />}
        />
        <Route
          path="contact"
          element={
            isAuthenticated ? <Navigate to="/app" replace /> : <Contact />
          }
        />
        <Route
          path="blog"
          element={
            isAuthenticated ? <Navigate to="/app" replace /> : <Blog />
          }
        />
        <Route
          path="blog/:slug"
          element={
            isAuthenticated ? <Navigate to="/app" replace /> : <BlogPost />
          }
        />

        <Route
          path="martial-arts-studio-software"
          element={
            isAuthenticated ? <Navigate to="/app" replace /> : <MetaLanding />
          }
        />

        <Route
          path="login"
          element={
            isAuthenticated ? (
              <Navigate to="/app" replace />
            ) : (
              <GeoBlock>
                <Login />
              </GeoBlock>
            )
          }
        />
        <Route
          path="signup"
          element={
            isAuthenticated ? (
              <Navigate to="/app" replace />
            ) : (
              <GeoBlock>
                <SignUp />
              </GeoBlock>
            )
          }
        />
        <Route
          path="staff-login/:studioCode"
          element={
            isAuthenticated ? (
              <Navigate to="/app" replace />
            ) : (
              <GeoBlock>
                <SubAccountLogin />
              </GeoBlock>
            )
          }
        />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        {/* 404 for public website routes */}
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* 404 Not Found Route */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <StripeProvider>
        <SocialMediaMeta />
        {stripePromise ? (
          <Elements stripe={stripePromise}>
            <Router>
              <ScrollToTop />
              <AppRoutes />
              <Toaster />
            </Router>
          </Elements>
        ) : (
          <Router>
            <ScrollToTop />
            <AppRoutes />
            <Toaster />
          </Router>
        )}
      </StripeProvider>
    </AuthProvider>
  );
}

export default App;
